import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Warning } from '@phosphor-icons/react';
import MilestoneModal, { MilestoneBadge } from './MilestoneModal';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/** Stylised solid-colour flame that gently flickers behind an active day. */
function FireGlyph({ size = 34 }) {
  return (
    <svg
      viewBox="0 0 32 32"
      width={size}
      height={size}
      aria-hidden
      className="flame-glyph"
    >
      <path
        d="M16 3.2c1.2 3.2 4.5 5.4 4.5 9.1 0 1.6-.5 3-1.3 4 1.8-.7 3.5-2.2 4.4-4.2.6 6.6-3 12.6-9 14.4-1.5.5-3.1.6-4.5.2C4.8 25.3 2 20.5 3 15.5c.6 2.1 2 3.7 3.5 4.5-.5-1.1-.8-2.3-.8-3.5C5.7 10.3 9.2 6.7 12 5c.5 1.1 1.3 2.1 2.2 2.6-.3-1.4-.4-2.9.1-4.4z"
        fill="currentColor"
      />
    </svg>
  );
}

const CSS = `
.flame-cell { position: relative; }
.flame-glyph { position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%);
  opacity: 0.9; transform-origin: 50% 55%; pointer-events: none;
  animation: flame-wiggle 1.35s ease-in-out infinite; will-change: transform; }
@keyframes flame-wiggle {
  0%,100% { transform: translate(-50%, -50%) scale(1) rotate(-2deg); filter: brightness(1.02); }
  35%     { transform: translate(-50%, -52%) scale(1.07) rotate(1.5deg); filter: brightness(1.12); }
  60%     { transform: translate(-50%, -48%) scale(0.98) rotate(-1deg); filter: brightness(0.98); }
}
@media (prefers-reduced-motion: reduce) { .flame-glyph { animation: none; } }
`;

export default function StreakWidget() {
  const [data, setData] = useState(null);
  const [modalWeeks, setModalWeeks] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API_URL}/progress/streak`, { withCredentials: true })
      .then(({ data }) => {
        if (!alive) return;
        setData(data);
        if (data.milestones_new?.length) setModalWeeks(data.milestones_new[data.milestones_new.length - 1]);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const closeModal = () => {
    setModalWeeks(null);
    axios.get(`${API_URL}/progress/streak`, { withCredentials: true }).then(({ data }) => setData(data)).catch(() => {});
  };

  if (!data) return null;
  const {
    current_days, best_days, week_days = [], workouts_this_week, at_risk,
    milestones = [7, 30, 100], milestones_earned = [],
  } = data;

  const isEmpty = current_days === 0 && best_days === 0;

  return (
    <div
      className={`ios-card p-4 mb-4 ${at_risk ? 'border-userRed/40 bg-userRed/[0.03]' : ''}`}
      data-testid="streak-widget"
    >
      <style>{CSS}</style>
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="text-lg font-black font-heading uppercase text-graphite">Streak</h2>
        {best_days > 0 && (
          <span
            className="text-[10px] uppercase tracking-[0.18em] font-bold text-graphite/50"
            data-testid="streak-best"
          >
            Best {best_days}d
          </span>
        )}
      </div>

      <div className="flex items-baseline gap-3 mb-3">
        <p
          className="text-5xl font-black font-heading tabular-nums text-graphite leading-none"
          data-testid="streak-current-days"
        >
          {current_days}
        </p>
        <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50">
          Day{current_days === 1 ? '' : 's'} in a row
        </p>
      </div>

      {/* Week strip — Mon–Sun with flame behind active days */}
      <div className="grid grid-cols-7 gap-1.5" data-testid="streak-week-strip">
        {week_days.map((d, i) => (
          <div
            key={i}
            className={`flame-cell flex items-center justify-center h-11 rounded-ios ${
              d.is_future ? 'bg-graphite/[0.04]' : d.active ? 'bg-userRed/10' : 'bg-graphite/[0.06]'
            }`}
            data-testid={`streak-day-${i}`}
            data-active={d.active}
            data-today={d.is_today}
          >
            {d.active && (
              <span className="flame-glyph text-userRed" aria-hidden>
                <FireGlyph size={40} />
              </span>
            )}
            <span
              className={`relative z-10 text-sm font-black font-heading uppercase leading-none ${
                d.active ? 'text-bone drop-shadow-[0_1px_0_rgba(0,0,0,0.35)]' :
                d.is_today ? 'text-userRed' :
                d.is_future ? 'text-graphite/25' : 'text-graphite/50'
              }`}
            >
              {d.letter}
            </span>
          </div>
        ))}
      </div>

      {at_risk ? (
        <div
          className="mt-3 flex items-start gap-2 rounded-ios p-2.5 bg-userRed/10 border border-userRed/25"
          data-testid="streak-at-risk"
        >
          <Warning size={14} weight="fill" className="text-userRed mt-0.5 flex-shrink-0" />
          <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-userRed leading-snug">
            Streak at risk — train today to keep your {current_days}-day streak alive
          </p>
        </div>
      ) : isEmpty ? (
        <p className="mt-3 text-[11px] uppercase tracking-[0.16em] font-bold text-graphite/50">
          Finish a workout today to start your streak
        </p>
      ) : (
        <p className="mt-3 text-[11px] uppercase tracking-[0.16em] font-bold text-graphite/60">
          {workouts_this_week} workout{workouts_this_week === 1 ? '' : 's'} this week
        </p>
      )}

      <div className="mt-4 flex gap-2" data-testid="milestone-shelf">
        {milestones.map((w) => (
          <MilestoneBadge
            key={w}
            weeks={w}
            earned={milestones_earned.includes(w)}
            testId={`milestone-badge-${w}`}
          />
        ))}
      </div>
      {modalWeeks && <MilestoneModal weeks={modalWeeks} onClose={closeModal} />}
    </div>
  );
}
